import { useCallback, useMemo } from 'react'
import { ImportUtilityModel } from '../AddImportUtilityDrawer/typings'
import {
  detectAndConvertDateTime,
  localizationDateFormat,
  localizationDateNoTimeZoneFormat,
} from '../functions'
import { format } from 'date-fns'
import { selectCurrentUser } from '@user/store/authorization/userSlice'
import { useSelector } from 'react-redux'
import { selectAllUsers } from '@user/store/users'
import { selectAllSalesFunnels } from '@user/store/salesFunnel'

export function useColumnHooks(importSettings: ImportUtilityModel | null) {
  const currentUser = useSelector(selectCurrentUser)
  const allUsers = useSelector(selectAllUsers)
  const allSalesFunnels = useSelector(selectAllSalesFunnels)

  const replaceValues = useCallback((values, defaultValue) => {
    return values.map((row) => ({
      index: row.index,
      value: defaultValue && !row.value ? defaultValue : row.value,
      info: [],
    }))
  }, [])

  const setValues = useCallback((values, value) => {
    return values.map((row) => ({
      index: row.index,
      value: value,
      info: [],
    }))
  }, [])

  const replaceCreatedDateTime = useCallback(
    (values) => {
      const defaultCreatedDateTime = format(new Date(), localizationDateFormat)

      return values.map((row) => ({
        index: row.index,
        value: row.value
          ? detectAndConvertDateTime(row.value)
          : defaultCreatedDateTime,
        info: [],
      }))
    },
    [replaceValues],
  )

  const replaceDates = useCallback(
    (values, defaultDate: any) => {
      if (defaultDate) {
        return replaceValues(
          values,
          format(defaultDate, localizationDateNoTimeZoneFormat),
        )
      } else {
        return replaceValues(values, null)
      }
    },
    [importSettings, replaceValues],
  )

  return useMemo(
    () => [
      {
        fieldName: 'submittedBy',
        callback: (values) => setValues(values, currentUser.userName),
      },
      {
        fieldName: 'createdBy',
        callback: (values) => setValues(values, currentUser.userName),
      },
      {
        fieldName: 'createdById',
        callback: (values) => setValues(values, currentUser.userId),
      },
      {
        fieldName: 'type',
        callback: (values) => setValues(values, 'Import'),
      },
      {
        fieldName: 'createdDateTime',
        callback: (values) => replaceCreatedDateTime(values),
      },
      {
        fieldName: 'note',
        callback: (values) => replaceValues(values, importSettings?.note),
      },
      {
        fieldName: 'firstName',
        callback: (values) => replaceValues(values, importSettings?.firstName),
      },
      {
        fieldName: 'lastName',
        callback: (values) => replaceValues(values, importSettings?.lastName),
      },
      {
        fieldName: 'companyName',
        callback: (values) =>
          replaceValues(values, importSettings?.companyName),
      },
      {
        fieldName: 'phoneNumber',
        callback: (values) =>
          replaceValues(values, importSettings?.phoneNumber),
      },
      {
        fieldName: 'phoneExt',
        callback: (values) => replaceValues(values, importSettings?.phoneExt),
      },
      {
        fieldName: 'emailAddress',
        callback: (values) =>
          replaceValues(values, importSettings?.emailAddress),
      },
      {
        fieldName: 'salesTeamName',
        callback: (values) =>
          replaceValues(values, importSettings?.salesTeamName),
      },
      {
        fieldName: 'assigneeId',
        callback: (values) => {
          if (!importSettings?.assigneeId) {
            return replaceValues(values, null)
          }

          const defaultAssignee = allUsers.find(
            (user) => user.id === importSettings.assigneeId,
          )

          const assigneeDisplayName = defaultAssignee
            ? `${defaultAssignee.fullName} - ${defaultAssignee.emailAddress}`
            : null

          return replaceValues(values, assigneeDisplayName)
        },
      },
      {
        fieldName: 'salesFunnelName',
        callback: (values) => {
          if (!importSettings?.salesFunnelName) {
            return replaceValues(values, null)
          }

          const defaultSalesFunnel = allSalesFunnels.find(
            (salesFunnel) =>
              salesFunnel.name === importSettings.salesFunnelName,
          )

          return replaceValues(
            values,
            defaultSalesFunnel?.isDefault
              ? `(Default) ${defaultSalesFunnel.name}`
              : importSettings.salesFunnelName,
          )
        },
      },
      {
        fieldName: 'leadFunnelStageName',
        callback: (values) =>
          replaceValues(values, importSettings?.leadFunnelStageName),
      },
      {
        fieldName: 'opportunityFunnelStageName',
        callback: (values) =>
          replaceValues(values, importSettings?.opportunityStageName),
      },
      {
        fieldName: 'serviceName',
        callback: (values) => {
          const { name, description } = importSettings?.service || {}
          const serviceName = name
            ? `${name}${description ? ` - ${description}` : ''}`
            : null

          return replaceValues(values, serviceName)
        },
      },
      {
        fieldName: 'frequencyName',
        callback: (values) => {
          const { description, annualOccurrences } =
            importSettings?.frequency || {}
          const frequencyName =
            description && annualOccurrences
              ? `${annualOccurrences} - ${description}`
              : null

          return replaceValues(values, frequencyName)
        },
      },
      {
        fieldName: 'initialPrice',
        callback: (values) =>
          replaceValues(values, importSettings?.initialPrice),
      },
      {
        fieldName: 'recurringPrice',
        callback: (values) =>
          replaceValues(values, importSettings?.recurringPrice),
      },
      {
        fieldName: 'quantity',
        callback: (values) => replaceValues(values, importSettings?.quantity),
      },
      {
        fieldName: 'nextActionDate',
        callback: (values) =>
          replaceDates(values, importSettings?.nextActionDate as any),
      },
      {
        fieldName: 'expectedClosingDate',
        callback: (values) =>
          replaceDates(values, importSettings?.expectedClosingDate as any),
      },
    ],
    [
      importSettings,
      replaceValues,
      replaceDates,
      setValues,
      currentUser,
      allUsers,
      allSalesFunnels,
    ],
  )
}
